import { Onboarding } from '@/resources/forge'

const store = {
  namespaced: true,
  
  state: {
    progress: null,
    loading: false,
    error: null,
    lastUpdated: null,
    badgeShown: localStorage.getItem('onboarding_badge_shown') === 'true',
    avatarNotificationShown: localStorage.getItem('onboarding_avatar_notification_shown') === 'true',
    showAvatarNotification: false
  },
  
  mutations: {
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    
    SET_PROGRESS(state, progress) {
      state.progress = progress
      state.lastUpdated = new Date()
      state.error = null
    },
    
    SET_ERROR(state, error) {
      state.error = error
      state.loading = false
    },
    
    CLEAR_PROGRESS(state) {
      state.progress = null
      state.error = null
      state.lastUpdated = null
    },
    
    SET_BADGE_SHOWN(state, shown) {
      state.badgeShown = shown
      if (shown) {
        localStorage.setItem('onboarding_badge_shown', 'true')
      } else {
        localStorage.removeItem('onboarding_badge_shown')
      }
    },
    
    SET_AVATAR_NOTIFICATION_SHOWN(state, shown) {
      state.avatarNotificationShown = shown
      if (shown) {
        localStorage.setItem('onboarding_avatar_notification_shown', 'true')
      } else {
        localStorage.removeItem('onboarding_avatar_notification_shown')
      }
    },
    
    SET_SHOW_AVATAR_NOTIFICATION(state, show) {
      state.showAvatarNotification = show
    }
  },
  
  actions: {
    async loadProgress({ commit }) {
      commit('SET_LOADING', true)
      try {
        const response = await Onboarding.progress()
        commit('SET_PROGRESS', response.body)
        return response.body
      } catch (error) {
        console.error('Failed to load onboarding progress:', error)
        commit('SET_ERROR', error.message || 'Failed to load onboarding progress')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    clearProgress({ commit }) {
      commit('CLEAR_PROGRESS')
    },
    
    markBadgeAsShown({ commit }) {
      commit('SET_BADGE_SHOWN', true)
    },
    
    resetBadgeShown({ commit }) {
      commit('SET_BADGE_SHOWN', false)
    },
    
    triggerAvatarNotification({ commit, state }) {
      // Only trigger if not already shown and user has completed onboarding
      if (!state.avatarNotificationShown && state.progress?.percentage === 100) {
        commit('SET_SHOW_AVATAR_NOTIFICATION', true)
      }
    },
    
    markAvatarNotificationAsShown({ commit }) {
      commit('SET_AVATAR_NOTIFICATION_SHOWN', true)
      commit('SET_SHOW_AVATAR_NOTIFICATION', false)
    },
    
    resetAvatarNotification({ commit }) {
      commit('SET_AVATAR_NOTIFICATION_SHOWN', false)
      commit('SET_SHOW_AVATAR_NOTIFICATION', false)
    }
  },
  
  getters: {
    completionPercentage: (state) => state.progress?.percentage || 0,
    nextTask: (state) => state.progress?.next_task,
    isCompleted: (state) => state.progress?.percentage === 100,
    completedCount: (state) => state.progress?.completed_count || 0,
    totalCount: (state) => state.progress?.total_count || 8,
    
    // Generate task list with completion status
    progressList: (state) => {
      if (!state.progress) return []
      
      const tasks = [
        { 
          id: 'has_projects', 
          title: 'Create your first project',
          description: 'Set up a project to organize your sites and team.',
          actionUrl: '/dashboard'
        },
        { 
          id: 'has_production_sites', 
          title: 'Create and configure a site',
          description: 'Create your first site and configure it for your needs.',
          actionUrl: '/dashboard'
        },
        { 
          id: 'has_user_deployed_versions', 
          title: 'Deploy your first version',
          description: 'Upload and deploy your site files to go live.',
          actionUrl: null // Will be set dynamically by API
        },
        { 
          id: 'has_version_comments', 
          title: 'Comment on a version',
          description: 'Collaborate with your team using version comments.',
          actionUrl: null // Will be set dynamically by API
        },
        { 
          id: 'has_forms', 
          title: 'Set up a form',
          description: 'Create forms to collect data from your site visitors.',
          actionUrl: null // Will be set dynamically by API
        },
        { 
          id: 'has_forms_with_responses', 
          title: 'Review form responses',
          description: 'Check the responses submitted through your forms.',
          actionUrl: null // Will be set dynamically by API
        },
        { 
          id: 'has_webhooks', 
          title: 'Configure webhooks',
          description: 'Set up webhooks to integrate with external services.',
          actionUrl: 'https://docs.getforge.com'
        },
        { 
          id: 'has_forgerc_configs', 
          title: 'Try Forgerc',
          description: 'Use our forge server configuration for fine tuning your deployment process.',
          actionUrl: 'https://docs.getforge.com/guides/introducing-forge-server/'
        }
      ]
      
      return tasks.map(task => ({
        ...task,
        completed: state.progress.progress[task.id] || false
      }))
    },
    
    // Get the next incomplete task
    nextIncompleteTask: (state, getters) => {
      const nextTaskFromApi = state.progress?.next_task
      if (nextTaskFromApi) {
        return {
          id: nextTaskFromApi.id,
          title: nextTaskFromApi.title,
          actionUrl: nextTaskFromApi.action_url
        }
      }
      
      // Fallback: find first incomplete task from our list
      return getters.progressList.find(task => !task.completed) || null
    },
    
    // Check if user should see badge animation
    shouldShowBadgeAnimation: (state, getters) => {
      return getters.isCompleted && !state.badgeShown
    },
    
    // Check if user has earned the badge (completed onboarding)
    hasBadge: (state, getters) => {
      return getters.isCompleted
    }
  }
}

export default store
